<template>
    <a-card :title="$t('screenTitle.coinChargeManage')">
        <a-collapse class="mb-20">
            <a-collapse-panel key="1" header="Lịch sử nạp tiền">
                <a href="http://ebank.seisea.com/dashboard/transactions" target="_blank">eBank</a>
            </a-collapse-panel>
            <a-collapse-panel key="2" header="Tỷ giá ngoại tệ">
                <a-table :dataSource="dataSourceVcbExchangeRate" :columns="columnsVcbExchangeRate" :loading="isLoadingVcbExchangeRate" :scroll="{y: '500px'}" :pagination="false" size="small">
                    <template #title>Cập nhật lúc (D/M/Y H:I:S): {{ vcbExchangeRateUpdatedAt }} <a-button type="primary" @click="() => fetchVcbExchangeRate()">Cập nhật</a-button></template>
                    <template #footer><a href="https://portal.vietcombank.com.vn/Personal/TG/Pages/ty-gia.aspx" target="_blank">VCB tỷ giá (Cột Chuyển Khoản)</a></template>
                </a-table>
            </a-collapse-panel>
            <a-collapse-panel v-if="googleSheetId.length" key="3" header="Thu chi">
                <a :href="`https://docs.google.com/spreadsheets/d/${googleSheetId}`" target="_blank">Google sheet</a>
            </a-collapse-panel>
        </a-collapse>
        <a-card class="mb-20">
            <a-form ref="form" :model="formModel" @finish="onFinish" :rules="formRule" :scrollToFirstError="true"
                layout="vertical">
                <a-form-item :label="$t('labelText.payment_username')" name="username">
                    <a-input v-model:value="formModel.username" />
                </a-form-item>

                <a-form-item :label="$t('labelText.payment_coin')" name="coin">
                    <template #help>
                        <div>{{ $t('text.preview')}}: <a-tag :color="((formModel.coin || 0) < 0) ? 'red' : 'green'">{{
                                number_format(formModel.coin) }}</a-tag>
                        </div>
                    </template>
                    <a-input-number v-model:value="formModel.coin" style="width: 200px" :disabled="(formModel.coin_promotion || formModel.point || 0) !== 0" />
                </a-form-item>

                <a-form-item :label="$t('labelText.payment_coin_promotion')" name="coin_promotion">
                    <template #help>
                        <div>{{ $t('text.preview')}}: <a-tag :color="((formModel.coin_promotion || 0) < 0) ? 'red' : 'green'">{{
                                number_format(formModel.coin_promotion) }}</a-tag>
                        </div>
                    </template>
                    <a-input-number v-model:value="formModel.coin_promotion" style="width: 200px" :disabled="(formModel.coin || formModel.point || 0) !== 0" />
                </a-form-item>

                <a-form-item :label="$t('labelText.payment_point')" name="point">
                    <template #help>
                        <div>{{ $t('text.preview')}}: <a-tag :color="((formModel.point || 0) < 0) ? 'red' : 'green'">{{
                                number_format(formModel.point) }}</a-tag>
                        </div>
                    </template>
                    <a-input-number v-model:value="formModel.point" :max="0" style="width: 200px" :disabled="(formModel.coin_promotion || formModel.coin || 0) !== 0" />
                </a-form-item>

                <a-form-item v-if="formModel.coin" :label="$t('labelText.payment_bank_owner')" name="bank_owner" :required="true">
                    <a-select v-model:value="formModel.bank_owner">
                        <a-select-option v-for="(v, i) in bankOwners" :value="v" :key="i">
                            <span>{{ v }}</span>
                        </a-select-option>
                    </a-select>
                </a-form-item>

                <a-form-item v-if="formModel.coin" :label="$t('labelText.payment_bank_type')" name="bank_type" :required="true">
                    <a-select v-model:value="formModel.bank_type">
                        <a-select-option v-for="(v, i) in [
                                'ATM',
                                'Paypal',
                                'Thẻ cào',
                                'KRWON - Hàn Quốc',
                                'Yên - Nhật Bản',
                                'Crypt Coin',
                            ]" :value="v" :key="i">
                            <span>{{ v }}</span>
                        </a-select-option>
                    </a-select>
                </a-form-item>

                <a-form-item>
                    <a-button type="primary" html-type="submit" block :loading="isSaving"
                        :disabled="!formModel.coin && !formModel.coin_promotion && !formModel.point">
                        <span>{{ $t('text.save') }}</span>
                    </a-button>
                </a-form-item>
            </a-form>
        </a-card>

        <a-table :dataSource="dataSource" :columns="columns" :pagination="pagination" :loading="isLoading" :scroll="{x: '100%'}"></a-table>
    </a-card>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, reactive, ref, watch, } from "vue";
import { useStore } from "vuex";

import { Button, Card, Form, Input, InputNumber, message, Select, Table, Tag, Collapse } from "ant-design-vue";

import { useI18n } from "vue-i18n";
import { RuleObject } from "ant-design-vue/lib/form";
import { ColumnType, TablePaginationConfig } from "ant-design-vue/lib/table";

import { RouteName } from "@/router";
import ApiRequest from "@/utils/ApiRequest";
import { number_format } from "@/utils/helpers";
import { parseTransInfo } from '@/views/payment/HistoryView.vue'
import { paymentStatusText } from "../payment/HistoryView.vue";
import jsonp from 'jsonp';

interface FormModel {
    username: string;
    coin?: number,
    coin_promotion?: number,
    point?: number,
    bank_owner: string,
    bank_type: string,
}

export default defineComponent({
    components: {
        ACard: Card,
        ATable: Table,
        AButton: Button,
        AForm: Form,
        AFormItem: Form.Item,
        AInput: Input,
        AInputNumber: InputNumber,
        ASelect: Select,
        ASelectOption: Select.Option,
        ATag: Tag,
        ACollapse: Collapse,
        ACollapsePanel: Collapse.Panel,

        // AppstoreAddOutlined,
        // DeleteOutlined,
    },

    setup() {
        const { t } = useI18n();
        const store = useStore();

        const googleSheetId = ref<string>('');
        const bankOwners = ref<string[]>([]);
        const hasMasterAdminPermission = computed(() => store.getters.hasMasterAdminPermission);

        const isLoading = ref(true);
        const dataSource = ref([]);
        const columns = ref<ColumnType[]>([
            {
                title: 'ID',
                dataIndex: 'id',
            },
            {
                title: t('labelText.payment_username'),
                dataIndex: ['user', 'name'],
            },
            {
                title: t('labelText.payment_status'),
                dataIndex: 'status',
                customRender: ({ text }) => {
                    return paymentStatusText(text);
                },
            },
            {
                title: t('labelText.payment_coin'),
                dataIndex: 'coin',
                customRender: ({ text }) => {
                    return number_format(text);
                },
            },
            {
                title: t('labelText.payment_coin_promotion'),
                dataIndex: 'coin_promotion',
                customRender: ({ text }) => {
                    return number_format(text);
                },
            },
            {
                title: t('labelText.payment_point'),
                dataIndex: 'point',
                customRender: ({ text }) => {
                    return number_format(text);
                },
            },
            {
                title: t('labelText.coin_charge_admin_add_executor'),
                dataIndex: ['executor', 'name'],
            },
            {
                title: t('labelText.created_at'),
                dataIndex: "created_at",
            }
        ]);
        const pagination = ref<TablePaginationConfig>({});
        const fetchData = async (page = 1) => {
            isLoading.value = true;

            return ApiRequest.get('/payments/admin-charge', {
                page,
            }).then(res => {
                const resData = res.data;

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dataSource.value = resData.data.map((v: any) => {
                    parseTransInfo(v);

                    return v;
                });

                ApiRequest.setPaginate(pagination.value, resData);
            })
            .finally(() => {
                isLoading.value = false;
            });
        };
        pagination.value.onChange = (page) => {
            fetchData(page);
        };

        const form = ref<typeof Form>();
        const isSaving = ref(false);
        const formModel = reactive<FormModel>({
            username: '',
            bank_owner: '',
            bank_type: '',
        });
        const formExtraError: Record<keyof FormModel, string> = {
            username: '',
            coin: '',
            coin_promotion: '',
            point: '',
            bank_owner: '',
            bank_type: '',
        };
        const formRule = reactive<Record<keyof FormModel, RuleObject[]>>({
            username: [
                {
                    required: true,
                    trigger: 'change',
                },
                {
                    validator: (_rule, _value, callback) => callback(formExtraError.username || undefined),
                },
            ],
            coin: [
                {
                    validator: (_rule, _value, callback) => callback(formExtraError.coin || undefined),
                },
            ],
            coin_promotion: [
                {
                    validator: (_rule, _value, callback) => callback(formExtraError.coin_promotion || undefined),
                },
            ],
            point: [
                {
                    validator: (_rule, _value, callback) => callback(formExtraError.point || undefined),
                },
            ],
            bank_owner: [],
            bank_type: [],
        });
        const onFinish = (values: FormModel) => {
            isSaving.value = true;
            ApiRequest.post('/payments/admin-charge', values).then(() => {
                message.success(t('message.createSuccess'));

                form.value?.resetFields();

                fetchData();
            })
                .catch(err => {
                    ApiRequest.handleError(err, formExtraError, form.value);
                })
                .finally(() => {
                    isSaving.value = false;
                });
        };

        const fetchPaymentGoogleSheetId = async () => {
            return ApiRequest.get('/payments/google-sheet-id').then(res => {
                googleSheetId.value = res.data.data;
            });
        };

        const fetchPaymentBankOwners = async () => {
            return ApiRequest.get('/payments/bank-owners').then(res => {
                bankOwners.value = res.data.data;
            });
        };

        const fetchDataInterval = setInterval(() => {
            if (!isSaving.value && !isLoading.value) {
                fetchData(pagination.value.current);
            }
        }, 60000);

        const isLoadingVcbExchangeRate = ref(true);
        const dataSourceVcbExchangeRate = ref([]);
        const columnsVcbExchangeRate = ref<ColumnType[]>([
            {
                title: 'Tên ngoại tệ',
                dataIndex: 'CurrencyName',
            },
            {
                title: 'Mã',
                dataIndex: 'CurrencyCode',
            },
            {
                title: 'Tỷ giá (1 Ngoại tệ = ? VND)',
                dataIndex: 'Transfer',
            },
        ]);
        const vcbExchangeRateUpdatedAt = ref<string>();
        const fetchVcbExchangeRate = () => {
            isLoadingVcbExchangeRate.value = true;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            jsonp('https://script.google.com/macros/s/AKfycbzfYi7VhVCsGmJeMMM57lKc_ptMqGP-IOinuYxLnS0i5sJKfvmcWjf3zSiSOd6Eh9JfTw/exec', {}, (err:any, data:any) => {
                if (err) {
                    // console.log(err);
                    dataSourceVcbExchangeRate.value = [];
                    vcbExchangeRateUpdatedAt.value = '';
                } else {
                    dataSourceVcbExchangeRate.value = data['rates'];
                    vcbExchangeRateUpdatedAt.value = data['updated_at'];
                }

                isLoadingVcbExchangeRate.value = false;
            });
        };
        const fetchDataIntervalVcbExchangeRate = setInterval(() => {
            if (!isLoadingVcbExchangeRate.value) {
                fetchVcbExchangeRate();
            }
        }, 60000);

        onMounted(() => {
            fetchData();

            if (hasMasterAdminPermission.value) {
                fetchPaymentGoogleSheetId();
            }

            fetchPaymentBankOwners();

            fetchVcbExchangeRate();
        });

        onUnmounted(() => {
            clearInterval(fetchDataInterval);
            clearInterval(fetchDataIntervalVcbExchangeRate);
        });

        watch(() => hasMasterAdminPermission.value, (val) => {
            if (val) {
                fetchPaymentGoogleSheetId();
            }
        });

        return {
            dataSource,
            columns,
            pagination,
            isLoading,
            fetchData,

            dataSourceVcbExchangeRate,
            columnsVcbExchangeRate,
            isLoadingVcbExchangeRate,
            vcbExchangeRateUpdatedAt,
            fetchVcbExchangeRate,

            googleSheetId,
            bankOwners,

            isSaving,
            formModel,
            formRule,
            form,
            onFinish,

            number_format,

            RouteName,
            hasMasterAdminPermission,
        };
    },
})
</script>

<style scoped>

</style>
